/* eslint-disable max-lines */
import { useCallback, useEffect, useMemo, useRef, useState } from "react"

import { useTranslation } from "react-i18next"
import { toast } from "sonner"

import type { WorkflowIssue } from "../utils/workflow-validation"
import type {
  ActivityMessage,
  ChatAppRequest,
  ServerMessage,
  WorkflowChatMessage,
} from "@/api/models"

import { explorerChatWebSocketApi } from "@/api/api"
import { VectorFieldOptions } from "@/api/models"
import {
  Bot,
  CheckCircle2,
  GripVertical,
  Loader2,
  MoreVertical,
  Send,
  Trash2,
  X,
} from "@/components/icons"
import { MarkdownRenderer } from "@/components/markdown-renderer"
import { Button } from "@/components/ui/button"
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu"
import { Input } from "@/components/ui/input"
import { ScrollArea } from "@/components/ui/scroll-area"
import { Separator } from "@/components/ui/separator"
import { SmallLogo } from "@/customer/small-logo"
import { useUpdateWorkflowChatHistory } from "@/features/ai-explorer/hooks/use-update-workflow-chat-history"
import { UserMessage } from "@/features/chat/components/user-message"
import styles from "@/features/chat/styles/answer.module.css"
import { safeImport } from "@/lib/chunk-reload"
import { cn } from "@/lib/utils"
import { SettingsService } from "@/services/settings-service"
import { translateActivityMessage } from "@/utils/activity-message-translator"

import { useWorkflowAiChatStore } from "../stores/workflow-ai-chat-store"
import { useWorkflowStore } from "../stores/workflow-store"
import { applyWorkflowOperations, type WorkflowOperation } from "../utils/apply-workflow-operations"
import { buildWorkflowBuilderUserMessage } from "../utils/build-workflow-builder-message"
import { buildAutoRetryPrompt, MAX_AUTO_RETRIES } from "../utils/workflow-ai-retry"
import { getWorkflowIssues } from "../utils/workflow-validation"

function getIssueKey(issue: WorkflowIssue): string {
  return `${issue.severity}:${issue.code}:${issue.nodeId ?? ""}:${issue.message}`
}

type WorkflowAiChatPanelProps = {
  workflowId?: string
  onSetMetadata?: (metadata: { title?: string; defaultProjectId?: string | null }) => void
}

function createMessage(role: WorkflowChatMessage["role"], content: string): WorkflowChatMessage {
  return {
    id: crypto.randomUUID(),
    role,
    content,
    created_at: new Date().toISOString(),
  }
}

export function WorkflowAiChatPanel({ workflowId, onSetMetadata }: WorkflowAiChatPanelProps) {
  const { t } = useTranslation()
  const [input, setInput] = useState("")
  const [isResizing, setIsResizing] = useState(false)
  const [activityMessage, setActivityMessage] = useState("")
  const resizeStartXRef = useRef<number | null>(null)
  const resizeStartWidthRef = useRef<number | null>(null)
  const abortControllerRef = useRef<AbortController | null>(null)
  const persistTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null)
  const messagesRef = useRef<WorkflowChatMessage[]>([])
  const lastPersistedLengthRef = useRef<number>(0)
  const autoRetryCountRef = useRef(0)
  const pendingRetryPromptRef = useRef<string | null>(null)

  const isOpen = useWorkflowAiChatStore((s) => s.isOpen)
  const panelWidth = useWorkflowAiChatStore((s) => s.panelWidth)
  const messages = useWorkflowAiChatStore((s) => s.messages)
  const isStreaming = useWorkflowAiChatStore((s) => s.isStreaming)
  const error = useWorkflowAiChatStore((s) => s.error)
  const activeWorkflowId = useWorkflowAiChatStore((s) => s.activeWorkflowId)
  const pendingPrompt = useWorkflowAiChatStore((s) => s.pendingPrompt)
  const setIsOpen = useWorkflowAiChatStore((s) => s.setIsOpen)
  const setPanelWidth = useWorkflowAiChatStore((s) => s.setPanelWidth)
  const addMessage = useWorkflowAiChatStore((s) => s.addMessage)
  const updateMessageContent = useWorkflowAiChatStore((s) => s.updateMessageContent)
  const setIsStreaming = useWorkflowAiChatStore((s) => s.setIsStreaming)
  const setError = useWorkflowAiChatStore((s) => s.setError)
  const clearPendingPrompt = useWorkflowAiChatStore((s) => s.clearPendingPrompt)
  const clearChat = useWorkflowAiChatStore((s) => s.clear)

  const nodes = useWorkflowStore((s) => s.nodes)
  const edges = useWorkflowStore((s) => s.edges)
  const applyGraphUpdate = useWorkflowStore((s) => s.applyGraphUpdate)
  const requestAutoLayout = useWorkflowStore((s) => s.requestAutoLayout)

  const updateChatMutation = useUpdateWorkflowChatHistory(workflowId ?? "")

  const workflowIssues = useMemo(() => getWorkflowIssues(nodes, edges), [nodes, edges])

  const isWorkflowSynced = !!workflowId && activeWorkflowId === workflowId
  const canSend = isWorkflowSynced && !isStreaming && input.trim().length > 0

  useEffect(() => {
    autoRetryCountRef.current = 0
    pendingRetryPromptRef.current = null
  }, [workflowId])

  // Keep ref in sync with messages for stable persistence callback
  useEffect(() => {
    messagesRef.current = messages
  }, [messages])

  // Debounced persistence - only when message count changes and not streaming
  useEffect(() => {
    if (!isWorkflowSynced) return
    if (isStreaming) return
    if (messages.length === 0) return
    if (messages.length === lastPersistedLengthRef.current) return

    if (persistTimerRef.current) {
      clearTimeout(persistTimerRef.current)
    }

    persistTimerRef.current = setTimeout(() => {
      persistTimerRef.current = null
      const currentMessages = messagesRef.current
      if (currentMessages.length > 0 && currentMessages.length !== lastPersistedLengthRef.current) {
        lastPersistedLengthRef.current = currentMessages.length
        updateChatMutation.mutate({ chat_history: currentMessages })
      }
    }, 1500)

    return () => {
      if (persistTimerRef.current) {
        clearTimeout(persistTimerRef.current)
        persistTimerRef.current = null
      }
    }
  }, [isWorkflowSynced, isStreaming, messages.length, updateChatMutation])

  const handleStartResize = useCallback(
    (event: React.PointerEvent<HTMLDivElement>) => {
      event.preventDefault()
      resizeStartXRef.current = event.clientX
      resizeStartWidthRef.current = panelWidth
      setIsResizing(true)
    },
    [panelWidth]
  )

  useEffect(() => {
    if (!isResizing) return

    const handlePointerMove = (event: PointerEvent) => {
      if (resizeStartXRef.current == null || resizeStartWidthRef.current == null) return
      const delta = resizeStartXRef.current - event.clientX
      setPanelWidth(resizeStartWidthRef.current + delta)
    }

    const handlePointerUp = () => {
      resizeStartXRef.current = null
      resizeStartWidthRef.current = null
      setIsResizing(false)
    }

    window.addEventListener("pointermove", handlePointerMove)
    window.addEventListener("pointerup", handlePointerUp)
    return () => {
      window.removeEventListener("pointermove", handlePointerMove)
      window.removeEventListener("pointerup", handlePointerUp)
    }
  }, [isResizing, setPanelWidth])

  const handleCancel = useCallback(() => {
    abortControllerRef.current?.abort()
    abortControllerRef.current = null
    setIsStreaming(false)
    setActivityMessage("")
  }, [setIsStreaming])

  const handleClearChat = useCallback(() => {
    if (!workflowId) return
    clearChat()
    lastPersistedLengthRef.current = 0
    updateChatMutation.mutate({ chat_history: [] })
    toast.success("Chat cleared")
  }, [workflowId, clearChat, updateChatMutation])

  const handleServerMessage = useCallback(
    (message: ServerMessage) => {
      if (message.type !== "activity") return

      const activityMsg = message as ActivityMessage

      // Handle workflow_edit activity - apply operations to the graph
      if (activityMsg.activity_type === "workflow_edit") {
        const details = activityMsg.details
        const targetWorkflowId =
          typeof details?.workflow_id === "string" ? details.workflow_id : null
        if (!targetWorkflowId || targetWorkflowId !== workflowId) return

        const rawOperations = details?.operations
        if (!Array.isArray(rawOperations)) return

        const currentNodes = useWorkflowStore.getState().nodes
        const currentEdges = useWorkflowStore.getState().edges
        const beforeIssues = getWorkflowIssues(currentNodes, currentEdges)

        const result = applyWorkflowOperations(
          rawOperations as WorkflowOperation[],
          currentNodes,
          currentEdges
        )

        if (result.errors.length > 0) {
          toast.error("Some AI changes could not be applied", { description: result.errors[0] })
        }

        if (result.metadataUpdate) {
          onSetMetadata?.(result.metadataUpdate)
        }

        const afterIssues = getWorkflowIssues(result.nodes, result.edges)
        const beforeErrorKeys = new Set(
          beforeIssues.filter((issue) => issue.severity === "error").map(getIssueKey)
        )
        const newValidationErrors = afterIssues.filter(
          (issue) => issue.severity === "error" && !beforeErrorKeys.has(getIssueKey(issue))
        )
        const hasOperationErrors = result.errors.length > 0
        const canAutoRetry = autoRetryCountRef.current < MAX_AUTO_RETRIES

        if (
          canAutoRetry &&
          pendingRetryPromptRef.current === null &&
          (hasOperationErrors || newValidationErrors.length > 0)
        ) {
          pendingRetryPromptRef.current = buildAutoRetryPrompt(result.errors, newValidationErrors)
        }

        applyGraphUpdate(result.nodes, result.edges)
        requestAutoLayout()
        const explanation = activityMsg.details?.explanation
        const systemMsg =
          typeof explanation === "string" && explanation
            ? `Applied changes: ${explanation}`
            : "Applied workflow changes."
        addMessage(createMessage("system", systemMsg))
        setActivityMessage("")
        return
      }
      const translatedMessage = translateActivityMessage(activityMsg, t)
      if (translatedMessage) {
        setActivityMessage(translatedMessage)
      }
    },
    [addMessage, applyGraphUpdate, requestAutoLayout, workflowId, onSetMetadata, t]
  )

  const sendPrompt = useCallback(
    async (prompt: string, selectedIssue?: WorkflowIssue, options?: { isAutoRetry?: boolean }) => {
      if (!workflowId) {
        toast.error("Save the workflow to use AI Builder.")
        return
      }

      const trimmed = prompt.trim()
      if (!trimmed) return

      const isAutoRetry = options?.isAutoRetry === true
      if (!isAutoRetry) {
        autoRetryCountRef.current = 0
        pendingRetryPromptRef.current = null
      }

      setError(null)
      setActivityMessage("")

      const userMessage = createMessage("user", trimmed)
      addMessage(userMessage)

      const assistantMessageId = crypto.randomUUID()
      addMessage({ ...createMessage("assistant", ""), id: assistantMessageId })

      setInput("")
      setIsStreaming(true)

      const abortController = new AbortController()
      abortControllerRef.current = abortController

      try {
        const settingsService = SettingsService.getInstance()
        const token = settingsService.token

        const outgoingUserContent = buildWorkflowBuilderUserMessage({
          workflowId,
          userPrompt: trimmed,
          nodes,
          edges,
          issues: workflowIssues,
          selectedIssue,
          interactionMode: "chat_edit",
        })

        const historyForModel: ChatAppRequest["messages"] = [
          ...messages
            .filter((m) => m.role === "user" || m.role === "assistant")
            .map((m) => ({
              role: m.role,
              content: m.content,
            })),
          { role: "user", content: outgoingUserContent },
        ]

        const request: ChatAppRequest = {
          messages: historyForModel,
          context: {
            overrides: {
              agent_name: "workflow_builder",
              use_custom_gpt: true,
              custom_gpt_model: "auto",
              vector_fields: [VectorFieldOptions.Embedding],
            },
          },
          stream: true,
          session_state: null,
        }

        const response = await explorerChatWebSocketApi(
          request,
          undefined,
          token,
          abortController.signal,
          handleServerMessage,
          "open",
          null
        )

        if (!response.body) {
          throw new Error("No response body")
        }

        const { default: readNDJSONStream } = await safeImport(
          () => import("ndjson-readablestream")
        )
        let accumulated = ""

        for await (const event of readNDJSONStream(response.body)) {
          if (!event) continue
          if (abortController.signal.aborted) {
            const abortError = new Error("AbortError")
            abortError.name = "AbortError"
            throw abortError
          }

          if (event?.error || event?.type === "error") {
            const errorMessage = String(event?.message || event?.error || "Unknown error")
            throw new Error(errorMessage)
          }

          if (typeof event.content === "string" && event.content.length > 0) {
            accumulated += event.content
            updateMessageContent(assistantMessageId, accumulated)
          }
        }
      } catch (err) {
        const message = err instanceof Error ? err.message : String(err)
        if (message !== "AbortError") {
          setError(message)
          toast.error("AI Builder error", { description: message })
        }
      } finally {
        setIsStreaming(false)
        setActivityMessage("")
        abortControllerRef.current = null
      }
    },
    [
      workflowId,
      addMessage,
      edges,
      handleServerMessage,
      messages,
      nodes,
      setError,
      setIsStreaming,
      updateMessageContent,
      workflowIssues,
    ]
  )

  useEffect(() => {
    if (!isWorkflowSynced) return
    if (isStreaming) return
    if (!pendingPrompt) return

    clearPendingPrompt()
    void sendPrompt(pendingPrompt)
  }, [clearPendingPrompt, isStreaming, isWorkflowSynced, pendingPrompt, sendPrompt])

  useEffect(() => {
    if (isStreaming) return
    const retryPrompt = pendingRetryPromptRef.current
    if (!retryPrompt) return
    if (autoRetryCountRef.current >= MAX_AUTO_RETRIES) {
      pendingRetryPromptRef.current = null
      const message = t("workflowLanding.aiPrompt.autoFixFailed")
      addMessage(createMessage("system", message))
      toast.error(message)
      return
    }
    pendingRetryPromptRef.current = null
    autoRetryCountRef.current += 1
    void sendPrompt(retryPrompt, undefined, { isAutoRetry: true })
  }, [addMessage, isStreaming, sendPrompt, t])

  const handleSend = useCallback(async () => {
    await sendPrompt(input)
  }, [input, sendPrompt])

  const handleAskToFix = useCallback(
    async (issue: WorkflowIssue) => {
      const nodeRef = issue.nodeId ? ` (node: ${issue.nodeId})` : ""
      const prompt = `Fix this workflow issue${nodeRef}:\n\n- ${issue.message}\n\nMake the smallest possible edits that fully resolve it.`
      await sendPrompt(prompt, issue)
    },
    [sendPrompt]
  )

  if (!isOpen) {
    return null
  }

  return (
    <div
      style={{ width: `${panelWidth}px`, maxWidth: "100%" }}
      className={cn(
        "bg-background relative flex h-full min-w-0 flex-col border-l",
        // On small screens, float over the canvas as a drawer instead of
        // consuming row width (which would hide the canvas entirely).
        "max-lg:absolute max-lg:inset-y-0 max-lg:right-0 max-lg:z-30 max-lg:!w-full max-lg:!max-w-sm max-lg:shadow-2xl"
      )}
    >
      <div
        aria-label="Resize AI panel"
        role="separator"
        className={cn(
          "absolute top-0 left-0 z-10 flex h-full w-3 items-center justify-center",
          isResizing ? "bg-muted" : "bg-transparent"
        )}
        onPointerDown={handleStartResize}
      >
        <GripVertical className="text-muted-foreground size-4" />
      </div>

      <div className="flex h-12 items-center gap-2 px-3">
        <Bot className="text-muted-foreground size-4" />
        <div className="text-sm font-medium">AI Builder</div>
        <div className="ml-auto flex items-center gap-2">
          {isStreaming && (
            <Button size="sm" variant="outline" onClick={handleCancel}>
              Stop
            </Button>
          )}
          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <Button aria-label="Chat options" size="icon" variant="ghost">
                <MoreVertical className="size-4" />
              </Button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end">
              <DropdownMenuItem
                disabled={!workflowId || isStreaming || messages.length === 0}
                onClick={handleClearChat}
              >
                <Trash2 className="mr-2 size-4" />
                Clear Chat
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
          <Button
            aria-label="Close AI Builder"
            size="icon"
            variant="ghost"
            onClick={() => setIsOpen(false)}
          >
            <X className="size-4" />
          </Button>
        </div>
      </div>
      <Separator />

      {isStreaming && (
        <div className="bg-muted/30 flex items-center gap-2 border-b px-3 py-2">
          <Loader2 className="text-primary size-3 animate-spin" />
          <span
            className={cn(
              "text-muted-foreground min-w-0 flex-1 text-xs [overflow-wrap:anywhere]",
              styles.loadingdots
            )}
          >
            {activityMessage || "Thinking"}
          </span>
        </div>
      )}

      <ScrollArea className="flex-1 p-3">
        <div className="flex flex-col gap-3">
          {!workflowId && (
            <div className="bg-muted/40 text-muted-foreground rounded-md border p-3 text-sm">
              Save the workflow to enable AI Builder.
            </div>
          )}

          {workflowIssues.map((issue) => (
            <div
              key={`${issue.code}-${issue.nodeId ?? "global"}`}
              className="bg-muted/20 flex items-start gap-2 rounded-md border p-2"
            >
              <div
                className={cn(
                  "mt-0.5 rounded px-1.5 py-0.5 text-[10px] font-medium",
                  issue.severity === "error"
                    ? "bg-destructive/10 text-destructive"
                    : "bg-warning/30 text-warning-ink"
                )}
              >
                {issue.severity.toUpperCase()}
              </div>
              <div className="min-w-0 flex-1 text-xs [overflow-wrap:anywhere]">
                {t(issue.messageKey, { defaultValue: issue.message, ...issue.messageParams })}
              </div>
              <Button
                disabled={!workflowId || isStreaming}
                size="sm"
                variant="outline"
                onClick={() => void handleAskToFix(issue)}
              >
                Fix
              </Button>
            </div>
          ))}

          {messages.map((message) => {
            if (message.role === "user") {
              return <UserMessage key={message.id} message={message.content} />
            }

            if (message.role === "assistant") {
              const isCurrentlyStreaming = isStreaming && message === messages[messages.length - 1]
              const showContent = message.content.length > 0

              return (
                <div key={message.id} className="flex items-start gap-3">
                  <div className="flex size-8 shrink-0 items-center justify-center">
                    <SmallLogo className="size-6" />
                  </div>
                  <div className="min-w-0 flex-1 overflow-hidden">
                    {showContent ? (
                      <MarkdownRenderer
                        content={message.content}
                        className={cn(
                          "prose-sm max-w-full! wrap-anywhere *:max-w-full **:wrap-anywhere",
                          isCurrentlyStreaming && "animate-pulse"
                        )}
                      />
                    ) : (
                      isCurrentlyStreaming && (
                        <div className="text-muted-foreground flex items-center gap-2 py-2 text-sm">
                          <span className={styles.loadingdots}>Generating response</span>
                        </div>
                      )
                    )}
                  </div>
                </div>
              )
            }

            return (
              <div
                key={message.id}
                className="border-success bg-success/15 flex items-start gap-2 rounded-lg border p-3"
              >
                <CheckCircle2 className="text-success mt-0.5 size-4 shrink-0" />
                <div className="text-foreground min-w-0 text-sm [overflow-wrap:anywhere]">
                  {message.content}
                </div>
              </div>
            )
          })}

          {error && (
            <div className="border-destructive/30 bg-destructive/10 rounded-md border p-3 text-sm">
              {error}
            </div>
          )}
        </div>
      </ScrollArea>

      <Separator />

      <div className="flex gap-2 p-3">
        <Input
          disabled={!workflowId || isStreaming}
          placeholder="Ask AI to build or edit this workflow…"
          value={input}
          onChange={(e) => setInput(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault()
              void handleSend()
            }
          }}
        />
        <Button aria-label="Send" disabled={!canSend} size="icon" onClick={() => void handleSend()}>
          <Send className="size-4" />
        </Button>
      </div>
    </div>
  )
}
